import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  Output,
} from '@angular/core';
import { FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface GroupedDropdownOption {
  label: string;
  value: string | number;
  disabled?: boolean;
}

export interface GroupedDropdownGroup {
  heading: string;
  options: GroupedDropdownOption[];
}

@Component({
    selector: 'app-grouped-dropdown',
    imports: [CommonModule, FormsModule],
    templateUrl: './grouped-dropdown.component.html',
    styleUrl: './grouped-dropdown.component.css',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => GroupedDropdownComponent),
            multi: true,
        },
    ]
})
export class GroupedDropdownComponent {
  @Input() groups: GroupedDropdownGroup[] = [
    {
      heading: 'Fruits',
      options: [
        { label: 'Apple', value: 'apple' },
        { label: 'Grapes', value: 'grapes' },
      ],
    },
  ];
  @Input() label = '';
  @Input() placeholder = 'Select option';
  @Input() searchPlaceholder = 'Search...';
  @Input() multiSelect = false;
  @Input() disabled = false;
  @Output() selectionChange = new EventEmitter<
    GroupedDropdownOption | GroupedDropdownOption[] | null
  >();

  isOpen = false;
  searchText = '';
  selectedValues: Array<string | number> = [];

  private onChange: (value: string | number | Array<string | number> | null) => void =
    () => {};
  private onTouched: () => void = () => {};

  constructor(private elementRef: ElementRef<HTMLElement>) {}

  get filteredGroups(): GroupedDropdownGroup[] {
    const query = this.searchText.trim().toLowerCase();

    if (!query) {
      return this.groups;
    }

    return this.groups
      .map((group) => ({
        ...group,
        options: group.options.filter((option) =>
          option.label.toLowerCase().includes(query),
        ),
      }))
      .filter((group) => group.options.length > 0);
  }

  get selectedLabel(): string {
    const selectedOptions = this.getSelectedOptions();

    if (!selectedOptions.length) {
      return this.placeholder;
    }

    if (!this.multiSelect) {
      return selectedOptions[0].label;
    }

    if (selectedOptions.length <= 2) {
      return selectedOptions.map((option) => option.label).join(', ');
    }

    return `${selectedOptions
      .slice(0, 2)
      .map((option) => option.label)
      .join(', ')} +${selectedOptions.length - 2}`;
  }

  writeValue(value: string | number | Array<string | number> | null): void {
    if (Array.isArray(value)) {
      this.selectedValues = [...value];
      return;
    }

    this.selectedValues = value === null || value === undefined ? [] : [value];
  }

  registerOnChange(
    fn: (value: string | number | Array<string | number> | null) => void,
  ): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  toggleDropdown(): void {
    if (this.disabled) return;

    this.isOpen = !this.isOpen;

    if (!this.isOpen) {
      this.clearSearch();
      this.onTouched();
    }
  }

  selectOption(option: GroupedDropdownOption): void {
    if (option.disabled) return;

    if (this.multiSelect) {
      this.selectedValues = this.isSelected(option)
        ? this.selectedValues.filter((value) => value !== option.value)
        : [...this.selectedValues, option.value];
    } else {
      this.selectedValues = [option.value];
      this.isOpen = false;
      this.clearSearch();
    }

    const value = this.multiSelect
      ? [...this.selectedValues]
      : this.selectedValues[0] ?? null;

    this.onChange(value);
    this.onTouched();
    this.selectionChange.emit(
      this.multiSelect ? this.getSelectedOptions() : this.getSelectedOptions()[0] ?? null,
    );
  }

  isSelected(option: GroupedDropdownOption): boolean {
    return this.selectedValues.includes(option.value);
  }

  clearSelection(event: MouseEvent): void {
    event.stopPropagation();
    this.selectedValues = [];
    this.onChange(this.multiSelect ? [] : null);
    this.onTouched();
    this.selectionChange.emit(this.multiSelect ? [] : null);
  }

  clearSearch(): void {
    this.searchText = '';
  }

  getSelectedOptions(): GroupedDropdownOption[] {
    const options = this.groups.flatMap((group) => group.options);
    return options.filter((option) => this.selectedValues.includes(option.value));
  }

  @HostListener('document:click', ['$event'])
  closeOnOutsideClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      this.isOpen = false;
      this.clearSearch();
      this.onTouched();
    }
  }

  @HostListener('document:keydown.escape')
  closeOnEscape(): void {
    this.isOpen = false;
    this.clearSearch();
  }
}
